import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle } from "lucide-react";

/**
 * useModalGuard — SATU SUMBER untuk perilaku modal, dipakai dari DALAM salah
 * satu dari lima pembungkus `Modal` lokal (documents/parts.jsx,
 * finance/CashbackProgram.jsx, finance/FinanceCreditNote.jsx,
 * finance/FinanceReimbursement.jsx, marketing/_shared.jsx). Menutup total 44
 * modal di 18 halaman lewat kelima berkas itu saja — jangan disalin ke
 * berkas lain; kalau modal baru butuh perilaku ini, PAKAI salah satu dari
 * lima pembungkus di atas, atau (kalau modal itu genuinely baru) impor hook
 * ini langsung, bukan menyalin isinya (CLAUDE.md: "kalau butuh sesuatu di
 * dua tempat, jadikan satu sumber" — komponen pemilih template pernah
 * tersalin 6x dan berpisah jalan, jangan diulang).
 *
 * MENYATUKAN EMPAT HAL, yang sebelumnya NOL berkas frontend punya:
 *   1. Escape menutup modal.
 *   2. Ctrl/Cmd+Enter menyimpan (memanggil `onSubmit`) — TAPI tidak kalau
 *      form belum valid (`disabled`) atau sedang menyimpan (`busy`), supaya
 *      tidak memicu kirim ganda.
 *   3. Klik di luar (backdrop) menutup modal — pola yang sudah ada di 214
 *      tempat lain di app ini; di sini disatukan lewat `requestClose` yang
 *      SAMA dipakai Escape, tombol X, dan tombol "Batal".
 *   4. Penjaga isian belum tersimpan, berlaku utuh untuk KETIGA jalan keluar
 *      di atas (Escape, klik-di-luar, tombol X/Batal) — BUKAN cuma Escape.
 *      Kalau hanya Escape yang dijaga sementara klik-di-luar tidak, lubangnya
 *      tetap terbuka (keputusan owner, lihat brief).
 *
 * CARA PAKAI (di dalam badan fungsi salah satu dari lima pembungkus Modal):
 *
 *   const guard = useModalGuard({ onClose, onSubmit, busy, disabled, dangerous });
 *   return (
 *     <div className="fixed inset-0 ..." onClick={guard.requestClose}>
 *       <div ref={guard.contentRef} onClick={e => e.stopPropagation()}>
 *         ...isi modal (header dengan tombol X onClick={guard.requestClose},
 *         children, footer dengan tombol Batal onClick={guard.requestClose}
 *         dan tombol submit onClick={onSubmit} SEPERTI BIASA — submit TIDAK
 *         lewat guard karena menyimpan bukan aksi yang membuang data)...
 *         {guard.confirming && (
 *           <UnsavedGuardBar onStay={guard.cancelClose} onDiscard={guard.confirmDiscard} />
 *         )}
 *       </div>
 *     </div>
 *   );
 *
 * Untuk pembungkus yang footer-nya berupa render-prop bebas (bukan
 * `onSubmit` langsung — pola `marketing/_shared.jsx`), oper `footerRef`
 * (ref ke elemen `<div>` footer, BUKAN ke seluruh modal) alih-alih
 * `onSubmit`. Ctrl+Enter akan meng-klik tombol TERAKHIR di dalam `footerRef`
 * — konvensi SERAGAM di seluruh pemanggil (Batal dulu, tombol aksi utama
 * PALING AKHIR, dan tombol itu sendiri sudah `disabled={busy...}` dari
 * halaman masing-masing) sudah diverifikasi di ke-20 modal Marketing yang
 * memakai pembungkus ini, jadi validasi & status busy otomatis terwarisi
 * TANPA hook ini perlu tahu apa-apa soal form itu. Tombol yang `disabled`
 * tidak memicu event click sama sekali (perilaku bawaan browser) — aman
 * dari kirim ganda dengan sendirinya (tapi lihat catatan `submitLockRef` di
 * bawah — `disabled` di DOM baru menyusul setelah render berikutnya, ada
 * jendela balap sebelum itu, dan lock sinkron di sini menutupnya).
 *
 * DETEKSI "ADA ISIAN BELUM DISIMPAN" — DUA LAPIS, TANPA state form dilacak
 * manual oleh pemanggil:
 *
 *   Lapis 1 — event bubbling native `input`/`change` di `contentRef`.
 *   Menangkap SEMUA pengetikan (input/textarea) dan `<select>` native.
 *   Murah, langsung, sudah benar sejak awal.
 *
 *   Lapis 2 — cuplikan nilai form sebelum/sesudah SETIAP klik di halaman
 *   (didengarkan di `document`, fase CAPTURE, jadi selalu terekam duluan
 *   sebelum handler React manapun sempat mengubah state). Ini menutup
 *   TITIK BUTA yang terbukti nyata pada Lapis 1 sendirian: banyak kontrol di
 *   app ini mengubah nilai lewat `onClick` + `setState` React murni, TANPA
 *   pernah mendispatch event DOM `input`/`change` — misalnya memilih tanggal
 *   di kalender (`components/ui/date-picker.jsx`, klik sel hari memanggil
 *   `onChange` React, bukan `input.dispatchEvent`), atau meng-klik baris
 *   kandidat yang mengisi field lain lewat `setF(...)` (mis.
 *   `CreditNoteDetail.jsx` `TerpakaiModal`, klik baris utang mengisi
 *   "Nominal Diterapkan"). Kedua kasus itu SUDAH DIVERIFIKASI lolos dari
 *   Lapis 1 dengan uji langsung (headless Chrome): backdrop/Escape/tombol X
 *   menutup modal seketika padahal field jelas sudah berubah.
 *
 *   Kenapa dibandingkan LEWAT KLIK, bukan diamati terus-menerus (polling/
 *   MutationObserver)? Karena `<input>` terkendali React menulis properti
 *   `.value` langsung ke node DOM tanpa lewat atribut — MutationObserver
 *   TIDAK melihat perubahan itu sama sekali (ini kelemahan React yang sudah
 *   dikenal luas, bukan bug DOM). Satu-satunya cara murah & akurat membaca
 *   nilai yang "sebenarnya" adalah membaca `.value`/`.checked` langsung dari
 *   elemen setelah React selesai commit — dan momen paling murah untuk
 *   memicu pembacaan itu adalah SETIAP klik, karena semua kasus titik buta
 *   di atas memang dipicu oleh klik (mouse ATAU keyboard — `<button>`
 *   native mendispatch `click` sungguhan saat diaktifkan lewat Enter/Space,
 *   jadi navigasi keyboard di kalender pun tertangkap tanpa kode tambahan).
 *   Klik yang TIDAK mengubah nilai apa pun (klik backdrop, klik tab, dst)
 *   hanya menghasilkan cuplikan sebelum == sesudah → tidak ditandai dirty.
 *
 *   Cuplikan "sebelum" diambil di fase CAPTURE (sebelum handler React
 *   manapun jalan), lalu cuplikan "sesudah" diambil lewat `setTimeout(0)`
 *   (bukan microtask) — sengaja menunggu satu giliran event-loop penuh
 *   supaya commit DOM React (yang dijadwalkan sebagai microtask oleh React
 *   di React 18) SUDAH PASTI selesai sebelum dibaca ulang.
 *
 *   KENAPA INI TIDAK MENGHASILKAN POSITIF PALSU untuk "nilai bawaan saat
 *   modal dibuka" atau "dimuat ulang dari server": kedua cuplikan SELALU
 *   berpasangan di sekitar satu klik yang sama — TIDAK ADA baseline statis
 *   yang diambil sekali saat mount lalu dibandingkan terus-menerus. Kalau
 *   data terisi lewat fetch async SEBELUM klik pertama terjadi, cuplikan
 *   "sebelum" pada klik itu sudah mencerminkan data yang baru dimuat itu —
 *   jadi perbandingannya otomatis mengisolasi HANYA perubahan akibat klik
 *   itu sendiri, bukan seluruh riwayat sejak modal dibuka. Modal yang tidak
 *   pernah diklik sama sekali (atau diklik tapi tak ada nilai yang berubah)
 *   tidak pernah ditandai dirty — pengujian manual poin 3 di bawah
 *   memverifikasi ini secara langsung.
 *
 *   Listener Lapis 2 HANYA dipasang kalau `dangerous` true (sama seperti
 *   Lapis 1) — modal lihat-saja/konfirmasi tidak menanggung biaya apa pun.
 *
 *   JALAN KELUAR EKSPLISIT — `markDirty()`: dikembalikan hook ini untuk
 *   kasus yang BENAR-BENAR tidak (dan tidak akan pernah) berwujud elemen
 *   form di DOM — mis. state yang cuma hidup di variabel JS (peta/objek,
 *   drag-and-drop kustom) dan tidak pernah tercermin sebagai `.value`
 *   sebuah `<input>`/`<select>`/`<textarea>`. TIDAK ADA pemanggil yang
 *   butuh ini hari ini (disisir — semua state form yang dilacak modal
 *   `dangerous` akhirnya bermuara ke sebuah kontrol form biasa), jadi ini
 *   murni jalan keluar untuk masa depan, BUKAN cara baru yang "disarankan"
 *   dipakai pemanggil — kalau nilainya toh muncul di sebuah `<input>`,
 *   Lapis 1+2 sudah menangkapnya otomatis TANPA `markDirty()` disentuh sama
 *   sekali, dan itu tetap jalan yang disukai (hook ini tidak menuntut
 *   apa-apa dari pemanggil; `markDirty` cuma katup pelepas, bukan beban).
 *
 * KAPAN pasang `dangerous={true}` pada satu instance <Modal>:
 *   - Form PANJANG (banyak field / daftar baris dinamis) ATAU yang
 *     menyentuh NOMINAL UANG yang diKETIK/dipilih staf sendiri (bukan
 *     sekadar konfirmasi angka yang sudah ditentukan sebelumnya).
 *   - JANGAN pasang di modal lihat-saja, cetak, konfirmasi ya/tidak biner,
 *     atau modal alasan satu baris (reject/cancel/close) — kehilangan satu
 *     kalimat gampang diketik ulang, dan konfirmasi yang kelewat sering
 *     bikin orang mengklik tanpa membaca (persis peringatan pemberi tugas).
 *   - Default `false` supaya SEMUA modal yang sudah memakai kelima
 *     pembungkus ini tetap tertutup LANGSUNG seperti sebelumnya, sampai
 *     satu titik panggil `<Modal>` secara eksplisit menambahkan
 *     `dangerous` — grep `dangerous` di kelima berkas pembungkus untuk
 *     lihat contoh yang sudah ditandai (Program Price Protection di
 *     CashbackProgram.jsx, Credit Note Baru di FinanceCreditNote.jsx, Klaim
 *     Reimbursement Baru di FinanceReimbursement.jsx).
 *
 * KENAPA Escape/Ctrl+Enter didengarkan di `document`, BUKAN di elemen modal
 * (`contentRef`): sudah dicoba dipasang di `contentRef` dan TERBUKTI SALAH
 * lewat uji langsung (headless Chrome) — begitu modal dibuka lewat klik
 * tombol pemicu ("Tambah", dst), FOKUS TETAP di tombol itu (di luar subtree
 * modal) sampai pengguna mengklik sesuatu DI DALAM modal. Escape yang
 * ditekan sebelum itu tidak pernah sampai ke listener yang dipasang di
 * `contentRef`, jadi "Escape menutup modal" gagal untuk kasus paling umum
 * (buka modal, langsung tekan Escape tanpa klik apa pun dulu). Konsekuensi
 * memasang di `document`: DatePicker/Popover di dalam modal yang menutup
 * dirinya sendiri lewat Escape (TIDAK stopPropagation) bisa ikut memicu
 * `requestClose` juga — trade-off yang diterima (bukan bug kehilangan data,
 * karena guard tetap menahan modal `dangerous` yang sudah dirty; modal
 * lihat-saja yang tertutup "double" pun tidak apa-apa karena memang itu
 * yang diinginkan pengguna saat menekan Escape).
 * Deteksi dirty (`input`/`change`, dan cuplikan klik Lapis 2) TETAP di-scope
 * ke `contentRef` — itu sengaja, supaya field di HALAMAN LAIN (di luar
 * modal) tidak ikut menandai modal ini dirty, walau listener-nya sendiri
 * terpasang di `document` (perlu itu supaya klik di dalam Popover/Portal —
 * yang secara DOM BUKAN anak `contentRef`, lihat `PopoverPrimitive.Portal`
 * di `components/ui/popover.jsx` — tetap memicu pembacaan ulang; yang
 * DIBACA tetap cuma elemen form di dalam `contentRef`).
 *
 * LAPISAN PALING ATAS YANG MENANG (Escape/Ctrl+Enter): beberapa halaman
 * menumpuk dialog konfirmasi HAND-ROLLED (bukan lewat hook ini) di ATAS
 * modal `useModalGuard` yang masih ter-mount di baliknya — dialog Ya/Tidak
 * PPN di Sales.jsx/InputNota.jsx/InventoryPO.jsx adalah contohnya (lihat
 * `useModalLayer` di bawah). Karena listener dipasang di `document`,
 * Escape/Ctrl+Enter yang ditekan sampai ke SEMUA listener yang terpasang,
 * termasuk punya modal yang sedang tertutupi secara visual. Tanpa penanda
 * "siapa paling atas", modal di baliknya ikut bereaksi — misalnya Escape
 * pada dialog PPN memicu palang "ada isian belum disimpan" pada modal Nota
 * di baliknya PADAHAL PENGGUNA TIDAK MEMINTA APA-APA (sudah terbukti
 * lewat uji langsung). `useModalGuard` mendaftarkan lapisannya sendiri ke
 * tumpukan module-level di bawah dan hanya menjawab Escape/Ctrl+Enter kalau
 * dialah lapisan PALING ATAS saat ini; dialog hand-rolled mendaftar lewat
 * `useModalLayer(active)` supaya modal di baliknya otomatis diam selama
 * dialog itu terbuka.
 *
 * JANGAN pakai hook ini untuk dialog konfirmasi biner (Ya/Tidak) yang
 * mengubah NOMINAL, seperti dialog PPN di Sales.jsx/InputNota.jsx/
 * InventoryPO.jsx — Escape di situ TIDAK BOLEH berarti "tidak". Modal-modal
 * itu hand-rolled (tidak memakai salah satu dari lima pembungkus ini) dan
 * di luar cakupan hook ini secara sengaja — tapi TETAP wajib panggil
 * `useModalLayer(active)` di titik itu (lihat di atas), supaya modal
 * `useModalGuard` di baliknya tidak ikut bereaksi terhadap Escape yang
 * sebenarnya ditujukan untuk dialog PPN.
 *
 * `active`: dipakai HANYA oleh pembungkus yang instance-nya tetap ter-mount
 * lintas buka/tutup (mis. `marketing/_shared.jsx` — polanya
 * `<Modal open={!!form}>` selalu dirender, `open` yang berubah, BUKAN
 * `{form && <Modal>}`). Oper `active={open}` di situ supaya listener &
 * status dirty dipasang ulang tiap kali modal dibuka lagi. Pembungkus lain
 * (yang componentnya cuma ada saat modal terbuka) boleh biarkan default
 * `true` — mount baru = state bersih dengan sendirinya.
 */
export function useModalGuard({
  onClose,
  onSubmit,
  busy = false,
  disabled = false,
  dangerous = false,
  active = true,
  footerRef,
} = {}) {
  const contentRef = useRef(null);
  const dirtyRef = useRef(false);
  const [confirming, setConfirming] = useState(false);
  // Kunci SINKRON (bukan state React) untuk Ctrl+Enter — lihat penjelasan
  // panjang di bawah, dekat handler keydown-nya.
  const submitLockRef = useRef(false);
  // Id lapisan modal INI di tumpukan module-level (lihat `useModalLayer`
  // & fungsi pushModalLayer/popModalLayer di bawah file ini).
  const layerIdRef = useRef(null);

  // Reset "dirty" & prompt konfirmasi setiap kali modal (kembali) aktif —
  // penting untuk pembungkus yang instance-nya persisten (marketing/_shared.jsx),
  // supaya sesi edit yang SUDAH dibuang tidak membuat sesi berikutnya (form
  // kosong yang baru) langsung dianggap dirty juga.
  useEffect(() => {
    if (!active) return;
    dirtyRef.current = false;
    setConfirming(false);
    submitLockRef.current = false;
  }, [active]);

  // Daftar sebagai satu lapisan modal selama `active` — dipakai keydown
  // handler di bawah untuk menolak menjawab Escape/Ctrl+Enter kalau ada
  // dialog lain (hand-rolled via `useModalLayer`, atau `useModalGuard` lain
  // yang dibuka belakangan) yang saat ini lebih atas.
  useEffect(() => {
    if (!active) return;
    const id = pushModalLayer();
    layerIdRef.current = id;
    return () => { popModalLayer(id); layerIdRef.current = null; };
  }, [active]);

  // Lapis 1 — lihat docstring di atas.
  useEffect(() => {
    if (!active || !dangerous) return;
    const el = contentRef.current;
    if (!el) return;
    const markDirtyFromEvent = () => { dirtyRef.current = true; };
    el.addEventListener("input", markDirtyFromEvent);
    el.addEventListener("change", markDirtyFromEvent);
    return () => {
      el.removeEventListener("input", markDirtyFromEvent);
      el.removeEventListener("change", markDirtyFromEvent);
    };
  }, [active, dangerous]);

  // Lapis 2 — cuplikan nilai form sebelum/sesudah tiap klik di halaman.
  // Lihat docstring panjang di atas untuk alasan desainnya (kenapa klik,
  // kenapa capture-phase, kenapa tidak jadi positif palsu untuk data bawaan
  // / dimuat ulang dari server).
  useEffect(() => {
    if (!active || !dangerous) return;
    let pendingTimer = null;
    const onClickCapture = () => {
      const el = contentRef.current;
      if (!el) return;
      const sebelum = snapshotFormValues(el);
      if (pendingTimer) clearTimeout(pendingTimer);
      pendingTimer = setTimeout(() => {
        pendingTimer = null;
        const elLagi = contentRef.current;
        if (!elLagi) return; // modal sudah dilepas selagi menunggu
        if (snapshotFormValues(elLagi) !== sebelum) dirtyRef.current = true;
      }, 0);
    };
    document.addEventListener("click", onClickCapture, true);
    return () => {
      document.removeEventListener("click", onClickCapture, true);
      if (pendingTimer) clearTimeout(pendingTimer);
    };
  }, [active, dangerous]);

  const requestClose = useCallback(() => {
    if (dangerous && dirtyRef.current) { setConfirming(true); return; }
    onClose?.();
  }, [dangerous, onClose]);

  const cancelClose = useCallback(() => setConfirming(false), []);
  const confirmDiscard = useCallback(() => { setConfirming(false); onClose?.(); }, [onClose]);

  // Jalan keluar eksplisit untuk state yang genuinely tidak berwujud form
  // control di DOM — lihat docstring "JALAN KELUAR EKSPLISIT" di atas.
  const markDirty = useCallback(() => { dirtyRef.current = true; }, []);

  useEffect(() => {
    if (!active) return;
    const handler = (e) => {
      // Bukan lapisan paling atas (ada dialog lain di atasnya, hand-rolled
      // via `useModalLayer` atau `useModalGuard` lain) — biarkan lapisan itu
      // yang menjawab, jangan ikut bereaksi (lihat docstring "LAPISAN PALING
      // ATAS YANG MENANG" di atas).
      if (!isTopModalLayer(layerIdRef.current)) return;
      if (e.key === "Escape") {
        e.stopPropagation();
        requestClose();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
        // Key-repeat dari tombol yang ditahan (OS mengirim keydown berulang)
        // BUKAN penekanan baru — abaikan, jangan ikut menghitung sebagai
        // percobaan submit kedua/ketiga.
        if (e.repeat) return;
        // Kunci sinkron: `busy`/`disabled` di closure baru berubah setelah
        // React re-render, jadi menahan Ctrl lalu menekan Enter berkali-kali
        // CEPAT (sebelum re-render pertama sempat terjadi) melewati
        // pengecekan `busy` yang MASIH `false` di semua closure itu —
        // terbukti menghasilkan 2-3 POST identik di FinanceReimbursement.jsx.
        // `submitLockRef` diset SINKRON di sini, jadi penekanan berikutnya
        // (walau closure `busy` masih basi) langsung ditolak.
        if (submitLockRef.current) return;
        if (onSubmit) {
          if (busy || disabled) return;
          e.preventDefault();
          submitLockRef.current = true;
          try {
            // `onSubmit` di semua pemanggil saat ini adalah fungsi async
            // (lihat CreditNoteDetail.jsx, FinanceReimbursement.jsx, dst) —
            // Promise.resolve(...) tetap aman kalau suatu saat ada pemanggil
            // sinkron (selesai di microtask berikutnya, bukan langsung).
            Promise.resolve(onSubmit()).finally(() => { submitLockRef.current = false; });
          } catch (err) {
            // Pemanggil yang throw SINKRON (bukan reject promise) tetap
            // wajib melepas kunci, kalau tidak modal ini terkunci permanen
            // dari Ctrl+Enter sampai ditutup & dibuka lagi.
            submitLockRef.current = false;
            throw err;
          }
        } else if (footerRef?.current) {
          const buttons = footerRef.current.querySelectorAll("button");
          const last = buttons[buttons.length - 1];
          // `last.disabled` dicek eksplisit juga di sini (bukan cuma
          // mengandalkan bahwa `.click()` pada tombol disabled no-op) supaya
          // kita tidak menahan `submitLockRef` untuk klik yang toh tidak
          // pernah terjadi.
          if (last && !last.disabled) {
            e.preventDefault();
            submitLockRef.current = true;
            last.click();
            // Tidak ada Promise untuk ditunggu di jalur ini (handler klik
            // tombol itu sepenuhnya milik halaman pemanggil, opaque bagi
            // hook ini) — lepas kunci setelah satu giliran event-loop,
            // cukup supaya atribut `disabled` React (kalau handler klik itu
            // men-set busy state) sempat ter-commit ke DOM sebelum kunci
            // kita sendiri lepas. Jendela balap yang tersisa sekecil ini
            // sudah jauh lebih sempit daripada tanpa kunci sama sekali.
            setTimeout(() => { submitLockRef.current = false; }, 0);
          }
        }
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [active, requestClose, onSubmit, busy, disabled, footerRef]);

  return { contentRef, requestClose, confirming, cancelClose, confirmDiscard, markDirty };
}

/**
 * Cuplikan nilai semua form control (`input`/`select`/`textarea`) di dalam
 * `root`, digabung jadi satu string. Dipakai HANYA untuk dibandingkan
 * (sebelum vs sesudah satu klik) — bukan untuk ditampilkan atau disimpan.
 * Checkbox/radio dibaca lewat `.checked` (bukan `.value`, yang buat
 * checkbox selalu "on"/string tetap terlepas dicentang atau tidak).
 * Sengaja TIDAK memakai `name`/`id` sebagai kunci (banyak input di app ini
 * tidak punya salah satunya) — urutan DOM dalam satu render sudah cukup
 * stabil untuk perbandingan sesaat seperti ini, dan field yang MUNCUL/HILANG
 * di antara dua cuplikan (mis. field yang baru dirender setelah sebuah baris
 * dipilih) otomatis membuat panjang string berbeda → terdeteksi berubah.
 */
function snapshotFormValues(root) {
  const els = root.querySelectorAll("input, select, textarea");
  let out = "";
  for (let i = 0; i < els.length; i++) {
    const el = els[i];
    out += "" + (el.type === "checkbox" || el.type === "radio" ? (el.checked ? "1" : "0") : el.value);
  }
  return out;
}

// ---- Tumpukan lapisan modal aktif (module-level) ----------------------
// Lihat docstring "LAPISAN PALING ATAS YANG MENANG" di useModalGuard di
// atas untuk alasannya. Tumpukan sederhana: TERAKHIR didaftarkan = paling
// atas. Module-level (bukan Context) karena dialog PPN hand-rolled yang
// perlu ikut serta (Sales.jsx/InputNota.jsx/InventoryPO.jsx) tidak berbagi
// pohon komponen React dengan modal `useModalGuard` yang mereka tumpuki —
// keduanya cuma perlu sepakat soal SATU tumpukan global, bukan lewat props.
let modalLayerSeq = 0;
const modalLayerStack = [];

function pushModalLayer() {
  const id = ++modalLayerSeq;
  modalLayerStack.push(id);
  return id;
}
function popModalLayer(id) {
  const idx = modalLayerStack.indexOf(id);
  if (idx !== -1) modalLayerStack.splice(idx, 1);
}
function isTopModalLayer(id) {
  return id != null && modalLayerStack.length > 0 && modalLayerStack[modalLayerStack.length - 1] === id;
}

/**
 * useModalLayer — daftarkan SATU lapisan modal HAND-ROLLED (dialog
 * konfirmasi PPN Ya/Tidak di Sales.jsx/InputNota.jsx/InventoryPO.jsx, dst —
 * yang sengaja TIDAK memakai useModalGuard karena Escape di situ tidak
 * boleh berarti "tidak", lihat docstring di atas) ke tumpukan yang sama
 * dipakai `useModalGuard`, supaya modal `useModalGuard` yang masih
 * ter-mount di BALIK dialog ini berhenti menanggapi Escape/Ctrl+Enter
 * selama dialog ini terbuka.
 *
 * Pakai dengan flag buka/tutup dialog itu sendiri, cukup satu baris di
 * komponen halaman:
 *   useModalLayer(!!ppnConfirm);
 *
 * TIDAK mengubah perilaku dialog itu sendiri sama sekali — dialog PPN tetap
 * menangani Escape/klik-di-luarnya sendiri persis seperti sekarang (mis.
 * backdrop yang sudah benar tidak pernah mengirim apa pun saat Escape,
 * dipertahankan apa adanya). Yang dicegah di sini HANYA reaksi modal LAIN
 * yang kebetulan masih ter-mount di baliknya.
 */
export function useModalLayer(active) {
  useEffect(() => {
    if (!active) return;
    const id = pushModalLayer();
    return () => popModalLayer(id);
  }, [active]);
}

/**
 * Banner konfirmasi "ada isian belum disimpan" — dirender oleh pembungkus
 * Modal sendiri saat `guard.confirming` true. SATU tempat, dipakai kelima
 * pembungkus, supaya markup & bahasanya tidak menyimpang satu sama lain.
 *
 * `onSaveDraft` (poin 14b brief owner 23 Agu 2026, opsional): tombol KETIGA
 * "Simpan sebagai Draf" — HANYA dirender kalau pemanggil mengoper prop ini.
 * Wajib opt-in per-pemanggil (BUKAN ditambahkan tanpa syarat): berkas ini
 * dipakai lewat lima pembungkus modal lain (documents/parts.jsx,
 * finance/CashbackProgram.jsx, FinanceCreditNote.jsx,
 * FinanceReimbursement.jsx, marketing/_shared.jsx) yang menutup total ~44
 * modal — kalau tombol ini muncul tanpa syarat, ke-44 modal itu mendadak
 * menawarkan "Simpan sebagai Draf" yang tidak punya arti di sana (tidak ada
 * endpoint draf untuk kredit nota/reimbursement/dst) dan kemungkinan besar
 * error saat diklik. Pemanggil pertama yang memakainya: `Kerangka` di
 * `pages/InventoryPO.jsx` (hanya modal "PO Baru", bukan Edit PO/Terima).
 */
export function UnsavedGuardBar({ onStay, onDiscard, onSaveDraft, testid = "modal-guard-bar" }) {
  return (
    <div className="px-5 py-3 border-t border-amber-200 bg-amber-50 flex flex-wrap items-center justify-between gap-2" data-testid={testid}>
      <p className="text-xs text-amber-800 flex items-center gap-1.5">
        <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> Ada isian belum disimpan. Tutup dan buang perubahan?
      </p>
      <div className="flex items-center gap-2 shrink-0">
        <button type="button" onClick={onStay} data-testid={`${testid}-stay`}
                className="h-8 px-3 rounded-lg border border-slate-200 text-xs font-medium text-slate-700 hover:bg-white">
          Lanjutkan Mengisi
        </button>
        {onSaveDraft && (
          <button type="button" onClick={onSaveDraft} data-testid={`${testid}-draft`}
                  className="h-8 px-3 rounded-lg border border-emerald-300 bg-emerald-50 text-xs font-medium text-emerald-700 hover:bg-emerald-100">
            Simpan sebagai Draf
          </button>
        )}
        <button type="button" onClick={onDiscard} data-testid={`${testid}-discard`}
                className="h-8 px-3 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-medium">
          Tutup &amp; Buang
        </button>
      </div>
    </div>
  );
}
